import React from 'react';
import usePropertySearch from './hooks/usePropertySearch';
import { getAreaDisplayName } from './utils/jobUtils';

/**
 * Property search component with dropdown functionality
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the parent modal is open
 * @param {Function} props.onPropertySelect - Callback when property is selected
 * @param {Object} props.selectedProperty - Currently selected property
 * @param {string} props.propertyId - Currently selected property ID
 * @param {Function} props.onPropertyIdChange - Callback when property ID changes
 * @returns {JSX.Element} PropertySearch component
 */
export default function PropertySearch({ 
  isOpen, 
  onPropertySelect, 
  selectedProperty: externalSelectedProperty, 
  propertyId,
  onPropertyIdChange 
}) {
  const {
    propertySearchTerm,
    selectedProperty,
    showPropertyDropdown,
    properties,
    propertiesLoading,
    propertySearchRef,
    handleSearchChange,
    handlePropertySelect,
    clearSelectedProperty,
    handleSearchFocus,
    setShowPropertyDropdown
  } = usePropertySearch(isOpen);

  // Use external selected property if provided, otherwise use internal state
  const currentSelectedProperty = externalSelectedProperty || selectedProperty;

  const handleInternalPropertySelect = (property) => {
    handlePropertySelect(property);
    onPropertySelect?.(property);
    onPropertyIdChange?.(property.id);
  };

  const handleClearProperty = () => {
    clearSelectedProperty();
    onPropertySelect?.(null);
    onPropertyIdChange?.('');
  };

  return (
    <div className="mb-4" ref={propertySearchRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Property * {propertiesLoading && <span className="ml-1 text-blue-500">(Loading...)</span>}
      </label>
      
      {currentSelectedProperty ? (
        <div className="mb-2 p-3 border rounded-md">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-medium">{currentSelectedProperty.name}</div>
              <div className="text-sm text-gray-500">{currentSelectedProperty.address}</div>
              {currentSelectedProperty.area && (
                <div className="text-sm text-blue-600 mt-1">
                  📍 {getAreaDisplayName(currentSelectedProperty.area)}
                </div>
              )}
              {currentSelectedProperty.propertyCode && (
                <div className="text-xs text-gray-400 mt-1">Code: {currentSelectedProperty.propertyCode}</div>
              )}
            </div>
            <button 
              type="button"
              onClick={handleClearProperty}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Clear selected property"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={propertySearchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={handleSearchFocus}
            placeholder="Search for property by name or code..."
            className="w-full p-2 border rounded-md"
            autoComplete="off"
          />
          
          {/* Property search dropdown */}
          {showPropertyDropdown && propertySearchTerm && (
            <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              {propertiesLoading ? (
                <div className="p-3 text-gray-500 flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading properties...
                </div>
              ) : properties?.length > 0 ? (
                properties.map(property => (
                  <div
                    key={property.id}
                    className="p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleInternalPropertySelect(property)}
                  >
                    <div className="font-medium">{property.name}</div>
                    <div className="text-sm text-gray-500">{property.address}</div>
                    {property.area && (
                      <div className="text-xs text-blue-600">📍 {getAreaDisplayName(property.area)}</div>
                    )}
                    {property.propertyCode && (
                      <div className="text-xs text-gray-400">Code: {property.propertyCode}</div>
                    )}
                  </div>
                ))
              ) : propertySearchTerm.length > 1 ? (
                <div className="p-3 text-gray-500">
                  No properties found. Try a different search term.
                </div>
              ) : (
                <div className="p-3 text-gray-500">
                  Type at least 2 characters to search for properties.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
} 